import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/AuthContext";
import { useI18n } from "@/i18n";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  // After a successful login, resume the purchase the visitor was trying to make.
  // NOTE: we only navigate here. The destination page (RecipeDetail / Bundles) is
  // responsible for clearing pending_purchase and continuing the checkout.
  useEffect(() => {
    if (!user) return;
    try {
      const intent = window.localStorage.getItem("pending_purchase");
      if (!intent) return;
      let parsed;
      try {
        parsed = JSON.parse(intent);
      } catch (_) {
        // Corrupt entry — clean it up so it doesn't linger forever.
        window.localStorage.removeItem("pending_purchase");
        return;
      }
      // Expire intents older than 30 minutes.
      const ageMs = Date.now() - (parsed?.at || 0);
      if (ageMs > 30 * 60 * 1000) {
        window.localStorage.removeItem("pending_purchase");
        return;
      }
      if (parsed?.path) {
        // Simple navigation intent (no action) — clear it now.
        if (!parsed.action) window.localStorage.removeItem("pending_purchase");
        nav(parsed.path);
      }
    } catch (_) {}
  }, [user, nav]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/me/purchases`).then(r => setItems(r.data));
  }, [user]);

  if (!user) return null;

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="dashboard-page">
      <div className="overline">{user.email}</div>
      <h1 className="font-serif text-5xl tracking-tighter mt-3">{t("page_dashboard_title")}</h1>
      <div className="divider-gold my-10" />
      {items.length === 0 ? (
        <div className="text-center py-20 glass p-10" data-testid="dashboard-empty">
          <p className="text-zinc-400">{t("page_dashboard_empty")}</p>
          <Link to="/recipes" className="btn-gold mt-6 inline-flex" data-testid="browse-collection-btn">{t("page_dashboard_browse")} →</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
          {items.map(({ purchase, recipe }) => {
            const tr = recipe[lang] || recipe.en;
            const img = recipe.image_b64 || recipe.image_url;
            return (
              <Link to={`/recipes/${recipe.id}`} key={purchase.id} className="card-recipe block" data-testid={`purchase-${recipe.id}`}>
                <img src={img} alt="" className="w-full h-56 object-cover" />
                <div className="p-5">
                  <div className="overline">{recipe.capital_city}</div>
                  <h3 className="font-serif text-xl mt-1">{tr.title}</h3>
                  <div className="mt-3 tag tag-gold inline-block">Unlocked ✓</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </main>
  );
}
